import { modelsApi } from '@/api/models.api';
import type { ModelSolid } from '@/types/project.types';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseImagePreviewProps {
  modelId: string | null;
  /** Which solid of the STEP document to preview. Both share `modelId`. */
  solid?: ModelSolid;
  disableGif?: boolean;
  enabled?: boolean;
}

export const useImagePreview = ({
  modelId,
  solid = 'model',
  disableGif = false,
  enabled = true,
}: UseImagePreviewProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [pngUrl, setPngUrl] = useState<string | null>(null);
  const [gifUrl, setGifUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const pngObjectUrlRef = useRef<string | null>(null);
  const gifObjectUrlRef = useRef<string | null>(null);

  // Tracks GIF fetch lifecycle so handleMouseEnter doesn't close over gifUrl state.
  // 'idle' → fetch not yet started; 'loading' → in-flight; 'loaded' → done.
  const gifFetchStateRef = useRef<'idle' | 'loading' | 'loaded'>('idle');

  // Fetch PNG as blob with auth headers
  useEffect(() => {
    if (!modelId) {
      // Cleanup previous URLs
      if (pngObjectUrlRef.current) {
        URL.revokeObjectURL(pngObjectUrlRef.current);
        pngObjectUrlRef.current = null;
      }
      if (gifObjectUrlRef.current) {
        URL.revokeObjectURL(gifObjectUrlRef.current);
        gifObjectUrlRef.current = null;
      }
      setPngUrl(null);
      setGifUrl(null);
      gifFetchStateRef.current = 'idle';
      return;
    }

    if (!enabled) {
      return;
    }

    const loadImages = async () => {
      setIsLoading(true);
      try {
        // Cleanup previous PNG URL
        if (pngObjectUrlRef.current) {
          URL.revokeObjectURL(pngObjectUrlRef.current);
        }

        const pngBlob = await modelsApi.fetchPngBlob(modelId, solid);

        const pngObjectUrl = URL.createObjectURL(pngBlob);
        pngObjectUrlRef.current = pngObjectUrl;
        setPngUrl(pngObjectUrl);
      } catch (error: any) {
        // If model was deleted (404), silently set pngUrl to null
        // Don't log error for 404 as it's expected when model is deleted
        if (error?.response?.status === 404) {
          setPngUrl(null);
        } else {
          console.error('Failed to load PNG preview:', error);
          setPngUrl(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadImages();

    // Cleanup object URLs on unmount or modelId/solid change
    return () => {
      if (pngObjectUrlRef.current) {
        URL.revokeObjectURL(pngObjectUrlRef.current);
        pngObjectUrlRef.current = null;
      }
      if (gifObjectUrlRef.current) {
        URL.revokeObjectURL(gifObjectUrlRef.current);
        gifObjectUrlRef.current = null;
      }
      // Reset fetch guard so a new modelId starts fresh
      gifFetchStateRef.current = 'idle';
    };
  }, [modelId, solid, enabled]);

  // Load GIF on first hover (only if not disabled).
  // Uses gifFetchStateRef instead of closing over gifUrl/isGifLoaded state,
  // so the callback identity stays stable across renders.
  const handleMouseEnter = useCallback(() => {
    if (disableGif) return;

    setIsHovered(true);

    // Guard via ref — fetch only once per modelId/solid lifetime
    if (modelId && gifFetchStateRef.current === 'idle') {
      gifFetchStateRef.current = 'loading';
      modelsApi
        .fetchGifBlob(modelId, solid)
        .then((gifBlob) => {
          // Cleanup previous GIF URL if exists
          if (gifObjectUrlRef.current) {
            URL.revokeObjectURL(gifObjectUrlRef.current);
          }

          const gifObjectUrl = URL.createObjectURL(gifBlob);
          gifObjectUrlRef.current = gifObjectUrl;
          setGifUrl(gifObjectUrl);
          gifFetchStateRef.current = 'loaded';
        })
        .catch((error) => {
          console.error('Failed to load GIF preview:', error);
          gifFetchStateRef.current = 'idle';
        });
    }
  }, [modelId, solid, disableGif]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return {
    pngUrl,
    gifUrl,
    isHovered,
    isLoading,
    handleMouseEnter,
    handleMouseLeave,
  };
};
